import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormControlDirective, FormControlName, FormGroupDirective, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { RegionCacheService } from '../../services/region.service';
import { Region } from '../../types/region.type';
import { OrganizationService } from '../../services/organization.service';

type Selection = string | string[];

@Component({
  selector: 'shared-country-select',
  templateUrl: './country-select.component.html',
  styleUrls: ['./country-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: CountrySelectComponent,
    },
  ],
  standalone: false,
})
export class CountrySelectComponent implements ControlValueAccessor, OnInit {
  @Input() multiple = false;
  @Input() searchEnabled = false;
  @Input() filterOrgCountryCodes: boolean;
  @Input() extraCountryCodes: string | string[];
  @Input() set value(value: Selection) {
    this._selectionModel = value;
    this.cdr.markForCheck();
  }

  @Output() valueChange = new EventEmitter<Selection>();
  @Output() closed = new EventEmitter();

  regions: Region[] = [];
  filteredRegions: Region[] = [];
  formControl: FormControl;
  disabled = false;
  selectedRegionNamesTooltip: string;

  private onChange: (value: Selection) => void = (value: Selection) => {};
  private onTouched: () => void = () => {};
  private touched = false;
  private _selectionModel: Selection;
  private _filter = '';

  get selectionModel(): Selection {
    return this._selectionModel;
  }

  set selectionModel(value: Selection) {
    this._selectionModel = value;
    this.valueChange.emit(value);
    if (this.multiple) {
      this.selectedRegionNamesTooltip = this.getSelectedRegionNames()?.join(', ');
    } else {
      this.selectedRegionNamesTooltip = this.getRegionName(value as string);
    }
    this.markAsTouched();
    this.onChange(value);
  }

  get filter(): string {
    return this._filter;
  }

  set filter(value: string) {
    this._filter = value;
    this.filterItems();
  }

  get isInvalid(): boolean {
    return this.formControl?.invalid && this.formControl?.touched;
  }

  @ViewChild('regionSelection') regionSelection: MatSelect;

  constructor(
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private regionService: RegionCacheService,
    private organizationService: OrganizationService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.regionService.initialize();
    if (this.filterOrgCountryCodes) {
      const orgCodes = this.organizationService.organizationConfigurationUi.nationalHolidays ?? [];
      const taskCodes = this.extraCountryCodes
        ? (Array.isArray(this.extraCountryCodes) ? this.extraCountryCodes : this.extraCountryCodes.split(','))
        : [];
      const allCodes = [...new Set([...orgCodes, ...taskCodes.filter(c => !!c)])];
      this.regions = this.regionService.getRegionsByIsoRegionNames(allCodes, true);
    } else {
      this.regions = this.regionService.regions;
    }
    this.filterItems();
    try {
      const ngControl = this.injector.get(NgControl);
      if (ngControl instanceof FormControlName) {
        this.formControl = this.injector.get(FormGroupDirective).getControl(ngControl);
      } else {
        this.formControl = (ngControl as FormControlDirective).form;
      }
    } catch {
      // component is not used as form control
    }

    this.selectedRegionNamesTooltip = this.getSelectedRegionNames()?.join(', ');
    this.cdr.markForCheck();
  }

  onBlur(): void {
    this.onTouched();
  }

  getRegionName(isoRegionName: string): string {
    return this.regionService.getRegionName(isoRegionName);
  }

  writeValue(value: Selection): void {
    this.value = value;
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
      this.cdr.markForCheck();
    }
  }

  joinRegionNames(regions: Region[]): string {
    return regions.map(r => r.englishName).join(', ');
  }

  private filterItems(): void {
    const filterValue = this.filter.toLowerCase();
    this.filteredRegions =
      this.filter === '' ? this.regions.slice() : this.regions.filter(i => i.englishName.toLowerCase().includes(filterValue) || i.isoRegionName.toLowerCase().includes(filterValue));
  }

  private getSelectedRegionNames(): string[] {
    return this.multiple ? (this.selectionModel as string[])?.map(r => this.getRegionName(r)) : [this.getRegionName(this.selectionModel as string)];
  }
}
